import { useRef, type ReactNode } from "react";
import "./otp.css";

/**
 * The one segmented numeric one-time-code input used everywhere the console asks for a code (sign-in,
 * TOTP enrollment, emailed codes, and every strict step-up dialog): `length` single-digit boxes
 * (default 6) laid out as 3 + 3. The controlled `value` is the concatenated digits (non-digits are
 * ignored), so callers keep a plain string and the same `/^\d{6}$/` checks.
 *
 * Typing a digit advances to the next box; Backspace clears and steps back; pasting or autofilling
 * the whole code into any box fills forward. It is numeric-only (`inputMode="numeric"`), so mobile
 * shows the number pad, and the first box carries `autocomplete="one-time-code"`. Fully
 * keyboard-operable with arrow keys and visible focus (design 12.4).
 *
 * Labelling: pass `id` and point the form's existing `<label htmlFor>` at it, so clicking the label
 * focuses the first box and the field keeps its label. The group itself is named
 * "<length>-digit authentication code", and each box "Digit N".
 */
export function OtpInput(props: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly length?: number;
  readonly disabled?: boolean;
  /** The first box's id, for an external `<label htmlFor>`. */
  readonly id?: string;
  /** Focus the first box on mount (the sign-in screens); off by default so dialogs keep their own focus order. */
  readonly autoFocus?: boolean;
  /** Mark every box required (native form validation). */
  readonly required?: boolean;
  /** Announce the code as invalid (for example after the server rejected it). */
  readonly invalid?: boolean;
  /** `center` (default, the sign-in card) or `start` (left-aligned forms and dialogs). */
  readonly align?: "center" | "start";
}): ReactNode {
  const length = props.length ?? 6;
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = props.value.replace(/\D/g, "").slice(0, length).split("");
  const cells = Array.from({ length }, (_, i) => digits[i] ?? "");

  const setDigit = (index: number, digit: string): void => {
    const next = cells.slice();
    next[index] = digit;
    props.onChange(next.join(""));
  };

  const focusBox = (index: number): void => {
    const el = refs.current[index];
    if (el) el.focus();
  };

  return (
    <div className="oa-otp" data-align={props.align ?? "center"} role="group" aria-label={`${length}-digit authentication code`}>
      {cells.map((cell, i) => (
        <input
          // eslint-disable-next-line react/no-array-index-key -- fixed-length positional boxes
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="oa-otp__box"
          {...(i === 0 && props.id !== undefined ? { id: props.id } : {})}
          {...(length % 2 === 0 && i === length / 2 ? { "data-group-break": "true" } : {})}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={i === 0 ? length : 1}
          value={cell}
          aria-label={`Digit ${i + 1}`}
          {...(props.invalid === true ? { "aria-invalid": true } : {})}
          {...(props.required === true ? { required: true } : {})}
          disabled={props.disabled === true}
          {...(i === 0 && props.autoFocus === true ? { autoFocus: true } : {})}
          onChange={(e) => {
            const only = e.target.value.replace(/\D/g, "");
            if (only.length === 0) {
              setDigit(i, "");
              return;
            }
            // A multi-char entry (paste, autofill or fast typing) fills forward from this box.
            if (only.length > 1) {
              const chars = only.slice(0, length - i).split("");
              const next = cells.slice();
              chars.forEach((c, k) => {
                next[i + k] = c;
              });
              props.onChange(next.join(""));
              focusBox(Math.min(i + chars.length, length - 1));
              return;
            }
            setDigit(i, only);
            if (i < length - 1) focusBox(i + 1);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              if (cells[i] === "" && i > 0) {
                setDigit(i - 1, "");
                focusBox(i - 1);
              } else {
                setDigit(i, "");
              }
            } else if (e.key === "ArrowLeft" && i > 0) {
              focusBox(i - 1);
            } else if (e.key === "ArrowRight" && i < length - 1) {
              focusBox(i + 1);
            }
          }}
          onPaste={(e) => {
            // Explicit paste handling so pasting the whole code fills the boxes: a paste into a
            // maxLength=1 input does not reliably fire onChange across browsers. Digits are laid in
            // from the first box.
            e.preventDefault();
            const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
            if (pasted.length === 0) return;
            props.onChange(pasted);
            focusBox(Math.min(pasted.length, length - 1));
          }}
          onFocus={(e) => e.target.select()}
        />
      ))}
    </div>
  );
}
